import React, { useState, useEffect } from 'react';
import { notificationsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Bell, Gear, PaperPlaneTilt, Plus, Trash, Envelope, CheckCircle,
  XCircle, At, Buildings, Users, ArrowRight, ShieldCheck, CreditCard,
  FileText, ClockCounterClockwise, Warning, Crown,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const NotificationsPage = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    email_enabled: true,
    // Hospital → Patient alerts
    claim_status_alerts: true,
    preauth_alerts: true,
    payment_alerts: true,
    document_reminders: true,
    // Platform → Hospital alerts
    platform_subscription_alerts: true,
    platform_config_alerts: true,
    platform_announcement_alerts: true,
    recipient_emails: [],
    patient_email_enabled: true,
  });
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [activeChannel, setActiveChannel] = useState('hospital-patient');

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [settingsRes, logsRes] = await Promise.all([
        notificationsApi.getSettings(),
        notificationsApi.getLogs()
      ]);
      setSettings(prev => ({ ...prev, ...settingsRes.data }));
      setLogs(logsRes.data);
    } catch (err) { toast.error('Failed to load notification settings'); }
    finally { setLoading(false); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await notificationsApi.updateSettings(settings);
      toast.success('Notification settings saved');
    } catch (err) { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };

  const addEmail = () => {
    if (!newEmail || !/\S+@\S+\.\S+/.test(newEmail)) { toast.error('Enter a valid email'); return; }
    if (settings.recipient_emails.includes(newEmail)) { toast.error('Email already added'); return; }
    setSettings(s => ({ ...s, recipient_emails: [...s.recipient_emails, newEmail] }));
    setNewEmail('');
  };

  const removeEmail = (email) => {
    setSettings(s => ({ ...s, recipient_emails: s.recipient_emails.filter(e => e !== email) }));
  };

  const sendTestEmail = async () => {
    if (!testEmail) { toast.error('Enter an email to send test'); return; }
    setSendingTest(true);
    try {
      const res = await notificationsApi.sendTest(testEmail);
      if (res.data.success) { toast.success('Test email sent successfully'); }
      else { toast.error('Failed to send test email. Check SMTP settings.'); }
    } catch (err) { toast.error('Failed to send test email'); }
    finally { setSendingTest(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="notifications-loading">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="notifications-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']" data-testid="notifications-title">Notifications</h1>
        <p className="text-sm text-slate-500 mt-1">Configure email communication channels</p>
      </div>

      {/* Channel Tabs */}
      <div className="flex border-b border-slate-200 gap-1">
        <button onClick={() => setActiveChannel('hospital-patient')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeChannel === 'hospital-patient' ? 'border-sky-500 text-sky-700 bg-sky-50/50' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-hospital-patient">
          <Buildings className="h-4 w-4" />
          Hospital
          <ArrowRight className="h-3 w-3" />
          <Users className="h-4 w-4" />
          Patient
        </button>
        {isSuperAdmin && (
        <button onClick={() => setActiveChannel('platform-hospital')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeChannel === 'platform-hospital' ? 'border-violet-500 text-violet-700 bg-violet-50/50' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-platform-hospital">
          <Crown className="h-4 w-4" />
          Platform
          <ArrowRight className="h-3 w-3" />
          <Buildings className="h-4 w-4" />
          Hospital
        </button>
        )}
        <button onClick={() => setActiveChannel('history')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeChannel === 'history' ? 'border-amber-500 text-amber-700 bg-amber-50/50' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-history">
          <ClockCounterClockwise className="h-4 w-4" />
          History
        </button>
      </div>

      {/* ============ HOSPITAL → PATIENT CHANNEL ============ */}
      {activeChannel === 'hospital-patient' && (
        <div className="space-y-5">
          {/* Channel Description */}
          <Card className="bg-gradient-to-r from-sky-50 to-blue-50 border border-sky-200">
            <CardContent className="p-4 flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
                <Users className="h-5 w-5 text-sky-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-sky-800">Hospital to Patient Communication</p>
                <p className="text-xs text-sky-600 mt-0.5">Email notifications sent to patients regarding their claims, pre-auth status, payments, and document requests</p>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Patient Alert Settings */}
            <Card className="bg-white border border-slate-200 shadow-sm" data-testid="patient-alert-settings">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold font-['Outfit'] flex items-center gap-2 text-slate-800">
                  <Gear className="h-4 w-4 text-sky-500" /> Patient Alert Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-sky-50 border border-sky-100">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Patient Email Alerts</p>
                    <p className="text-[10px] text-slate-500">Master toggle for all patient emails</p>
                  </div>
                  <Switch checked={settings.patient_email_enabled !== false}
                    onCheckedChange={v => setSettings(s => ({ ...s, patient_email_enabled: v }))}
                    data-testid="toggle-patient-email" />
                </div>

                {[
                  { key: 'claim_status_alerts', label: 'Claim Status Changes', desc: 'Notify patient when claim status updates', icon: FileText, color: 'hover:bg-sky-50' },
                  { key: 'preauth_alerts', label: 'Pre-Auth Updates', desc: 'Pre-auth approval/rejection notifications', icon: ShieldCheck, color: 'hover:bg-blue-50' },
                  { key: 'payment_alerts', label: 'Payment Confirmations', desc: 'Payment received/processed notifications', icon: CreditCard, color: 'hover:bg-emerald-50' },
                  { key: 'document_reminders', label: 'Document Reminders', desc: 'Remind patients to submit pending documents', icon: Warning, color: 'hover:bg-amber-50' },
                ].map(item => (
                  <div key={item.key} className={`flex items-center justify-between p-3 rounded-lg transition-colors ${item.color}`}>
                    <div className="flex items-center gap-3">
                      <item.icon className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-sm font-medium text-slate-700">{item.label}</p>
                        <p className="text-[10px] text-slate-500">{item.desc}</p>
                      </div>
                    </div>
                    <Switch checked={settings[item.key]}
                      onCheckedChange={v => setSettings(s => ({ ...s, [item.key]: v }))}
                      data-testid={`toggle-${item.key}`} />
                  </div>
                ))}

                <Button onClick={saveSettings} disabled={saving}
                  className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white mt-2" data-testid="save-patient-settings">
                  {saving ? 'Saving...' : 'Save Patient Alert Settings'}
                </Button>
              </CardContent>
            </Card>

            {/* Patient Recipient Emails */}
            <div className="space-y-5">
              <Card className="bg-white border border-slate-200 shadow-sm" data-testid="recipient-emails-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold font-['Outfit'] flex items-center gap-2 text-slate-800">
                    <Envelope className="h-4 w-4 text-sky-500" /> Hospital Staff Recipients
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-slate-500">Staff who also receive copies of patient notification emails</p>
                  <div className="flex gap-2">
                    <Input placeholder="staff@hospital.com" value={newEmail}
                      onChange={e => setNewEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addEmail()}
                      className="border-slate-300" data-testid="new-recipient-email" />
                    <Button onClick={addEmail} size="sm" className="bg-sky-500 hover:bg-sky-600 text-white px-3" data-testid="add-email-button">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {(settings.recipient_emails || []).length === 0 ? (
                      <p className="text-xs text-slate-400 text-center py-3">No additional recipients</p>
                    ) : (settings.recipient_emails || []).map((email, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-sky-50 rounded px-3 py-1.5 border border-sky-100" data-testid={`recipient-email-${idx}`}>
                        <span className="text-xs text-slate-700">{email}</span>
                        <button onClick={() => removeEmail(email)} className="text-red-400 hover:text-red-600"><Trash className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Test Email */}
              <Card className="bg-white border border-slate-200 shadow-sm" data-testid="test-email-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold font-['Outfit'] flex items-center gap-2 text-slate-800">
                    <PaperPlaneTilt className="h-4 w-4 text-emerald-500" /> Send Test Email
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-slate-500">Verify your SMTP configuration by sending a test email</p>
                  <div className="flex gap-2">
                    <Input placeholder="test@example.com" value={testEmail}
                      onChange={e => setTestEmail(e.target.value)} className="border-slate-300" data-testid="test-email-input" />
                    <Button onClick={sendTestEmail} disabled={sendingTest}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-4" data-testid="send-test-email-button">
                      {sendingTest ? 'Sending...' : 'Send'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* ============ PLATFORM → HOSPITAL CHANNEL (Super Admin only) ============ */}
      {activeChannel === 'platform-hospital' && isSuperAdmin && (
        <div className="space-y-5">
          <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200">
            <CardContent className="p-4 flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                <Buildings className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-violet-800">Platform to Hospital Communication</p>
                <p className="text-xs text-violet-600 mt-0.5">Emails from Care'O platform to hospital administrators for subscription updates, configuration changes, and important announcements</p>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card className="bg-white border border-slate-200 shadow-sm" data-testid="platform-alert-settings">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold font-['Outfit'] flex items-center gap-2 text-slate-800">
                  <Gear className="h-4 w-4 text-violet-500" /> Platform Alert Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-violet-50 border border-violet-100">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Platform Emails</p>
                    <p className="text-[10px] text-slate-500">Master toggle for all platform-to-hospital emails</p>
                  </div>
                  <Switch checked={settings.email_enabled}
                    onCheckedChange={v => setSettings(s => ({ ...s, email_enabled: v }))}
                    data-testid="toggle-email-enabled" />
                </div>

                {[
                  { key: 'platform_subscription_alerts', label: 'Subscription & Billing', desc: 'Renewal reminders, plan changes, payment confirmations', icon: Crown, color: 'hover:bg-amber-50' },
                  { key: 'platform_config_alerts', label: 'Configuration Updates', desc: 'Module changes, user limit updates, feature activations', icon: Gear, color: 'hover:bg-blue-50' },
                  { key: 'platform_announcement_alerts', label: 'Platform Announcements', desc: 'New features, maintenance notices, important updates', icon: Bell, color: 'hover:bg-violet-50' },
                ].map(item => (
                  <div key={item.key} className={`flex items-center justify-between p-3 rounded-lg transition-colors ${item.color}`}>
                    <div className="flex items-center gap-3">
                      <item.icon className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-sm font-medium text-slate-700">{item.label}</p>
                        <p className="text-[10px] text-slate-500">{item.desc}</p>
                      </div>
                    </div>
                    <Switch checked={settings[item.key] !== false}
                      onCheckedChange={v => setSettings(s => ({ ...s, [item.key]: v }))}
                      data-testid={`toggle-${item.key}`} />
                  </div>
                ))}

                <Button onClick={saveSettings} disabled={saving}
                  className="w-full bg-gradient-to-r from-violet-500 to-purple-600 text-white mt-2" data-testid="save-platform-settings">
                  {saving ? 'Saving...' : 'Save Platform Alert Settings'}
                </Button>
              </CardContent>
            </Card>

            {/* SMTP Info */}
            <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 h-fit" data-testid="smtp-config-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium flex items-center gap-2 text-amber-800">
                  <At className="h-4 w-4 text-amber-600" /> SMTP Configuration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-amber-700 mb-3">Configure Gmail SMTP in Settings to enable email delivery:</p>
                <div className="bg-white/60 rounded-lg p-3 border border-amber-200 font-mono text-[10px] space-y-0.5 text-amber-900">
                  <div><span className="text-amber-600">SMTP_HOST</span>=smtp.gmail.com</div>
                  <div><span className="text-amber-600">SMTP_PORT</span>=587</div>
                  <div><span className="text-amber-600">SMTP_USER</span>=your-email@gmail.com</div>
                  <div><span className="text-amber-600">SMTP_PASSWORD</span>=your-app-password</div>
                </div>
                <p className="text-[10px] text-amber-600 mt-2">
                  Get App Password: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="underline font-medium">Google Account</a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ============ NOTIFICATION HISTORY ============ */}
      {activeChannel === 'history' && (
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="notification-logs-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold font-['Outfit'] flex items-center gap-2 text-slate-800">
              <Bell className="h-4 w-4 text-amber-500" /> Notification History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="text-center py-16 text-slate-500" data-testid="no-notifications">
                <Bell className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p className="text-sm font-medium">No notifications sent yet</p>
                <p className="text-xs mt-1 text-slate-400">Notifications will appear here when email alerts are triggered</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {logs.map((log, idx) => (
                  <div key={log.id || idx} className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 transition-colors" data-testid={`notification-log-${idx}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{log.subject}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{log.recipient || log.to}</p>
                      </div>
                      {log.status === 'sent' ? (
                        <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] ml-2">Sent</Badge>
                      ) : log.status === 'mocked' ? (
                        <Badge className="bg-sky-100 text-sky-700 border-0 text-[10px] ml-2">Mocked</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 border-0 text-[10px] ml-2">Failed</Badge>
                      )}
                    </div>
                    {log.created_at && <p className="text-[10px] text-slate-400 mt-1.5">{new Date(log.created_at).toLocaleString('en-IN')}</p>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default NotificationsPage;
